import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
    CheckCircle2, AlertTriangle, XCircle, MinusCircle, RefreshCw, Clock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 30_000;

const STATUS_META = {
    operational:    { icon: CheckCircle2,  color: "text-emerald-600", ring: "ring-emerald-200", bg: "bg-emerald-50",  label: "Operational" },
    degraded:       { icon: AlertTriangle, color: "text-amber-600",   ring: "ring-amber-200",   bg: "bg-amber-50",    label: "Degraded"   },
    outage:         { icon: XCircle,       color: "text-rose-600",    ring: "ring-rose-200",    bg: "bg-rose-50",     label: "Outage"     },
    not_configured: { icon: MinusCircle,   color: "text-slate-400",   ring: "ring-slate-200",   bg: "bg-slate-50",    label: "Not configured" },
};

function StatusPill({ status }) {
    const m = STATUS_META[status] || STATUS_META.not_configured;
    const Icon = m.icon;
    return (
        <span
            data-testid={`status-pill-${status}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${m.bg} ${m.color} ${m.ring}`}
        >
            <Icon className="h-3.5 w-3.5" />
            {m.label}
        </span>
    );
}

function OverallBanner({ overall }) {
    const m = STATUS_META[overall] || STATUS_META.not_configured;
    const Icon = m.icon;
    const headline = {
        operational:    "All systems operational",
        degraded:       "Some systems experiencing issues",
        outage:         "Major outage detected",
        not_configured: "Some services not configured",
    }[overall] || "Status unavailable";
    return (
        <div
            data-testid="status-overall-banner"
            className={`flex items-start gap-4 rounded-2xl border p-6 md:p-8 ${m.bg} border-transparent ring-1 ${m.ring}`}
        >
            <Icon className={`mt-0.5 h-8 w-8 flex-shrink-0 ${m.color}`} />
            <div>
                <h2 className={`text-2xl font-semibold ${m.color}`}>{headline}</h2>
                <p className="mt-1 text-sm text-slate-600">
                    Live view of the Simonara Talent Platform's public integrations.
                    This page refreshes automatically every 30 seconds.
                </p>
            </div>
        </div>
    );
}

function ComponentRow({ c }) {
    const m = STATUS_META[c.status] || STATUS_META.not_configured;
    return (
        <div
            data-testid={`status-row-${c.key}`}
            className="flex items-center justify-between gap-4 border-t border-slate-200 py-4 first:border-0"
        >
            <div className="flex items-center gap-3">
                <div className={`h-2.5 w-2.5 rounded-full ${m.color.replace('text-', 'bg-')}`} />
                <div>
                    <div className="text-sm font-semibold text-slate-900" data-testid={`status-label-${c.key}`}>
                        {c.label}
                    </div>
                    <div className="text-xs text-slate-500">{c.detail}</div>
                </div>
            </div>
            <div className="flex items-center gap-3">
                {c.latency_ms > 0 && (
                    <span className="hidden text-xs text-slate-400 sm:inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {c.latency_ms} ms
                    </span>
                )}
                <StatusPill status={c.status} />
            </div>
        </div>
    );
}

export default function StatusPage() {
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [lastFetched, setLastFetched] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/status`, { timeout: 8000 });
            setData(res.data);
            setError("");
            setLastFetched(new Date());
        } catch (e) {
            setError(e?.message || "Could not reach the status API.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(load, POLL_MS);
        return () => clearInterval(t);
    }, [load]);

    return (
        <div className="min-h-screen bg-slate-50 py-12 sm:py-16" data-testid="status-page">
            <div className="mx-auto max-w-3xl px-6">
                <div className="mb-8 flex items-end justify-between">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700">
                            Simonara · Status
                        </p>
                        <h1 className="mt-2 text-3xl font-semibold text-slate-900 sm:text-4xl">
                            Platform status
                        </h1>
                    </div>
                    <button
                        onClick={load}
                        disabled={loading}
                        className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-60"
                        data-testid="status-refresh-button"
                    >
                        <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </button>
                </div>

                {error && (
                    <div
                        data-testid="status-error"
                        className="mb-6 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"
                    >
                        Failed to load status: {error}
                    </div>
                )}

                {data && (
                    <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <OverallBanner overall={data.overall} />

                        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h3 className="mb-2 text-sm font-semibold text-slate-900">Components</h3>
                            <div>
                                {data.components.map((c) => (
                                    <ComponentRow key={c.key} c={c} />
                                ))}
                            </div>
                        </div>

                        <p className="mt-6 text-center text-xs text-slate-500">
                            Last updated {lastFetched ? lastFetched.toLocaleTimeString() : "—"}
                            {" · "}
                            Report an incident at{" "}
                            <a
                                href="mailto:hello@simonaratalent.us"
                                className="text-emerald-700 underline underline-offset-2"
                            >
                                hello@simonaratalent.us
                            </a>
                        </p>
                    </motion.div>
                )}
            </div>
        </div>
    );
}
